import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconSparkles } from '@tabler/icons-react';
import {
  ArrowUp,
  CalendarDays,
  Maximize2,
  Minimize2,
  Pause,
  Sparkles,
  Zap,
  X,
} from 'lucide-react';
import RelatoriosOryaMarkdownView from '../../relatorios/components/RelatoriosOryaMarkdownView';
import OryaThinkingStatus from '../../documentation/components/OryaThinkingStatus';
import { DIARIO_ORYA_QUICK_PROMPTS } from '../constants/diarioOryaQuickPrompts';
import { postDiarioOryaChat } from '../services/technicianPanelApi';
import '../../documentation/DocumentationOryaPanel.css';
import './TechnicianDiarioOryaPanel.css';

function createMessageId() {
  return `diario-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatDateLabel(ymd) {
  if (!ymd) return '';
  try {
    const [y, m, d] = String(ymd).split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return ymd;
  }
}

function firstName(full) {
  const s = String(full || '').trim();
  if (!s) return '';
  return s.split(/\s+/)[0];
}

function QuickIcon({ name }) {
  if (name === 'bolt') return <Zap size={16} strokeWidth={1.75} aria-hidden />;
  if (name === 'sparkle') return <Sparkles size={16} strokeWidth={1.75} aria-hidden />;
  return <CalendarDays size={16} strokeWidth={1.75} aria-hidden />;
}

function buildHistory(messages) {
  const items = (messages || []).filter((m) => !m.isError);
  const history = [];
  for (let i = 0; i + 1 < items.length; i += 2) {
    const user = items[i];
    const assistant = items[i + 1];
    if (user?.role !== 'user' || assistant?.role !== 'assistant') continue;
    const u = String(user.content || '').trim();
    const a = String(assistant.content || '').trim();
    if (!u || !a) continue;
    history.push({ role: 'user', content: u });
    history.push({ role: 'assistant', content: a });
  }
  return history;
}

/** Remove separadores --- / *** e limpa cabeçalhos Horário (BSB/Brasília). */
function sanitizeDiarioAssistantMarkdown(raw) {
  return String(raw || '')
    .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, '')
    .replace(
      /(\|\s*)Hor[aá]rio(?:\s*\((?:BSB|Bras[ií]lia|Brasília)\))?(\s*\|)/gi,
      '$1Horário$2'
    )
    .replace(
      /(\|\s*)Hora(?:\s*\((?:BSB|Bras[ií]lia|Brasília)\))?(\s*\|)/gi,
      '$1Horário$2'
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Painel lateral Orya — Diário técnico (Rastreio).
 */
export default function TechnicianDiarioOryaPanel({
  open,
  onClose,
  technicianId,
  technicianName,
  chatUserName,
  dateYmd,
  timeFrom,
  timeTo,
}) {
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [visible, setVisible] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const abortRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      setClosing(false);
      setMessages([]);
      setInput('');
      setExpanded(false);
      return undefined;
    }
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, [open]);

  useEffect(() => {
    if (!open || !visible) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 280);
    return () => clearTimeout(t);
  }, [open, visible]);

  useEffect(() => {
    const bumpMap = () => {
      try {
        window.dispatchEvent(new Event('resize'));
        const g = window.google;
        if (!g?.maps?.event) return;
        document.querySelectorAll('[data-tpm="map"]').forEach((mapRoot) => {
          const map = mapRoot.__tpmGmap?.map;
          if (map) g.maps.event.trigger(map, 'resize');
        });
      } catch {
        /* ignore */
      }
    };
    if (!open) {
      bumpMap();
      return undefined;
    }
    const t = setTimeout(bumpMap, 340);
    return () => clearTimeout(t);
  }, [open, visible, expanded, closing]);

  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, busy]);

  const handleClose = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose?.();
    }, 280);
  }, [onClose]);

  const sendPrompt = useCallback(
    async (rawPrompt) => {
      const prompt = String(rawPrompt || '').trim();
      if (!prompt || busy || !technicianId || !dateYmd) return;

      const userMsg = { id: createMessageId(), role: 'user', content: prompt };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      setBusy(true);

      const ac = new AbortController();
      abortRef.current = ac;

      try {
        const history = buildHistory([...messages, userMsg]);
        const data = await postDiarioOryaChat(
          technicianId,
          {
            date: dateYmd,
            prompt,
            history: history.slice(0, -1),
            timeFrom,
            timeTo,
            technicianName,
            chatUserName,
          },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        setMessages((prev) => [
          ...prev,
          {
            id: createMessageId(),
            role: 'assistant',
            content:
              sanitizeDiarioAssistantMarkdown(data.content) || 'Sem resposta.',
          },
        ]);
      } catch (e) {
        if (ac.signal.aborted || e?.name === 'AbortError') return;
        setMessages((prev) => [
          ...prev,
          {
            id: createMessageId(),
            role: 'assistant',
            isError: true,
            content: e.message || 'Não foi possível consultar o Diário técnico.',
          },
        ]);
      } finally {
        if (abortRef.current === ac) abortRef.current = null;
        if (!ac.signal.aborted) setBusy(false);
      }
    },
    [busy, technicianId, technicianName, chatUserName, dateYmd, timeFrom, timeTo, messages]
  );

  const onSubmit = useCallback(
    (e) => {
      e?.preventDefault?.();
      sendPrompt(input);
    },
    [input, sendPrompt]
  );

  const handleBackToStart = useCallback(() => {
    if (busy) return;
    setMessages([]);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [busy]);

  const hasPdfContent = messages.some(
    (m) => !m.isError && m.role === 'assistant' && String(m.content || '').trim()
  );

  const handleExportPdf = useCallback(async () => {
    if (busy || exportingPdf || !hasPdfContent) return;
    setExportingPdf(true);
    try {
      const { exportOryaDiarioAnswerToPdf } = await import(
        '../../relatorios/utils/relatoriosOryaAnswerExport'
      );
      await exportOryaDiarioAnswerToPdf(messages, { dateYmd, technicianName });
    } catch {
      /* silencioso */
    } finally {
      setExportingPdf(false);
    }
  }, [busy, exportingPdf, hasPdfContent, messages, dateYmd, technicianName]);

  if (!open) return null;

  const showWelcome = messages.length === 0 && !busy;
  const greet = firstName(technicianName);

  return (
    <aside
      className={[
        'tpm-diario-orya',
        visible && !closing ? 'tpm-diario-orya--visible' : '',
        expanded ? 'tpm-diario-orya--expanded' : '',
        closing ? 'tpm-diario-orya--closing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Diário técnico Orya"
    >
      <header className="tpm-diario-orya-header">
        <div className="tpm-diario-orya-brand">
          <IconSparkles size={18} stroke={1.75} className="tpm-diario-orya-brand-ico" aria-hidden />
          <span>Orya</span>
          <span className="tpm-diario-orya-brand-sub">Diário técnico</span>
        </div>
        <div className="tpm-diario-orya-header-actions">
          <button
            type="button"
            className="tpm-diario-orya-icon-btn"
            aria-label={expanded ? 'Recolher painel' : 'Expandir painel'}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <Minimize2 size={15} strokeWidth={2} /> : <Maximize2 size={15} strokeWidth={2} />}
          </button>
          <button
            type="button"
            className="tpm-diario-orya-icon-btn"
            aria-label="Fechar"
            onClick={handleClose}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
      </header>

      <div className="tpm-diario-orya-meta" role="status">
        <span>{formatDateLabel(dateYmd)}</span>
        {(timeFrom || timeTo) && (
          <span>
            {timeFrom || '00:00'} → {timeTo || '23:59'}
          </span>
        )}
      </div>

      <div className="tpm-diario-orya-body" ref={bodyRef}>
        {showWelcome ? (
          <div className="tpm-diario-orya-welcome">
            <IconSparkles size={36} stroke={1.5} className="tpm-diario-orya-welcome-ico" aria-hidden />
            <h2 className="tpm-diario-orya-welcome-title">
              {greet ? `Olá` : 'Diário técnico'}
              {greet ? <span className="tpm-diario-orya-welcome-name"> — contexto {greet}</span> : null}
            </h2>
            <p className="tpm-diario-orya-welcome-sub">O que aconteceu neste dia no app Go?</p>
            <ul className="tpm-diario-orya-quick">
              {DIARIO_ORYA_QUICK_PROMPTS.map((q) => (
                <li key={q.id}>
                  <button
                    type="button"
                    className="tpm-diario-orya-quick-btn"
                    disabled={busy}
                    onClick={() => sendPrompt(q.label)}
                  >
                    <span className="tpm-diario-orya-quick-ico">
                      <QuickIcon name={q.icon} />
                    </span>
                    <span>{q.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="tpm-diario-orya-thread">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`tpm-diario-orya-msg tpm-diario-orya-msg--${m.role}${
                  m.isError ? ' is-error' : ''
                }`}
              >
                {m.role === 'assistant' ? (
                  <div className="tpm-diario-orya-msg-md">
                    <RelatoriosOryaMarkdownView markdown={m.content} canExport={false} />
                  </div>
                ) : (
                  <p className="tpm-diario-orya-msg-text">{m.content}</p>
                )}
              </div>
            ))}
            {busy ? (
              <div className="tpm-diario-orya-thinking">
                <OryaThinkingStatus
                  active={busy}
                  steps={[
                    { text: 'Consultando o log do app…', duration: 2200 },
                    { text: 'Cruzando evidências…', duration: 2800 },
                    { text: 'Montando a resposta…', duration: null },
                  ]}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="tpm-diario-orya-dock">
        {hasPdfContent ? (
          <>
            <button
              type="button"
              className="tpm-diario-orya-dock-btn"
              onClick={handleExportPdf}
              disabled={busy || exportingPdf}
              title={busy ? 'Aguarde a Orya terminar a resposta' : 'Baixar PDF da conversa'}
            >
              {exportingPdf ? 'Gerando PDF…' : 'Baixar PDF'}
            </button>
            <span className="tpm-diario-orya-dock-dot" aria-hidden />
          </>
        ) : null}
        <button
          type="button"
          className="tpm-diario-orya-dock-btn"
          onClick={handleBackToStart}
          disabled={busy || messages.length === 0}
          title={
            busy
              ? 'Aguarde a Orya terminar a resposta'
              : messages.length === 0
                ? 'Você já está na tela inicial'
                : 'Voltar à tela inicial'
          }
        >
          Voltar ao início
        </button>
      </div>

      <form className="tpm-diario-orya-composer" onSubmit={onSubmit}>
        <textarea
          ref={inputRef}
          className="tpm-diario-orya-input"
          rows={1}
          value={input}
          disabled={busy}
          placeholder="Pergunte sobre a linha do tempo deste dia…"
          onChange={(e) => {
            setInput(e.target.value);
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit(e);
            }
          }}
        />
        <div className="tpm-diario-orya-composer-bar">
          <span className="tpm-diario-orya-skill-pill" title="Skill: log do app Go">
            <Zap size={12} strokeWidth={2} aria-hidden />
            Log do app
          </span>
          {busy ? (
            <button
              type="button"
              className="tpm-diario-orya-send tpm-diario-orya-send--busy"
              aria-label="Parar"
              onClick={() => {
                abortRef.current?.abort();
                abortRef.current = null;
                setBusy(false);
              }}
            >
              <span className="tpm-diario-orya-send-busy" aria-hidden>
                <span className="tpm-diario-orya-send-spinner" />
                <Pause size={12} strokeWidth={2.5} fill="currentColor" />
              </span>
            </button>
          ) : (
            <button
              type="submit"
              className="tpm-diario-orya-send"
              disabled={!input.trim()}
              aria-label="Enviar"
            >
              <ArrowUp size={16} strokeWidth={2.4} />
            </button>
          )}
        </div>
      </form>
    </aside>
  );
}
